/** Entry point of the phase-D life package: registers objects, talk, drawers and day hooks. Imported once from main.ts. */
import '../ui/life.css';
import '../render/propsLife';
import { bus } from '../core/bus';
import { allChoresDone, apply, celebrate, doChore, onceToday, toast } from '../core/effects';
import { hasGame, S } from '../core/game';
import { dayId } from '../core/state';
import { m } from '../i18n';
import { say } from '../ui/dialog';
import { layers } from '../ui/layers';
import { openEnglish } from '../ui/english';
import { takeNewGameGrade } from '../ui/start';
import { openWallet } from '../ui/wallet';
import { openSaveCode } from '../ui/lifeSave';
import { exhibitReward, openExhibition, openWorksheet } from '../ui/worksheet';
import { completeObjective } from './days';
import { logJournal } from './journal';
import { allowanceDialog, impulseAd, interestDialog, piggyBank, whenFree, wishMorning } from './lifeMoney';
import { allowanceDue, life } from './money';
import { registerMenuItem, registerObjects } from './registry';

const min = (n: number) => m().common.minutes(n);

/** Same rule as the built-in chores: all six done unlocks a one-off daily reward. */
function choreCheck(): void {
  if (!allChoresDone() || !onceToday('allChores')) return;
  window.setTimeout(() => {
    const t = m().chores;
    say({
      who: t.allTitle,
      icon: '🌟',
      text: t.allText,
      choices: [{ text: t.allClaim, run: () => (celebrate(true), apply({ stars: 20, coins: 50, mood: 30, love: { mom: 20 } })) }],
    });
  }, 700);
}

/** The study desk: the homework chore is now a real worksheet for your grade. */
function homeDesk(): void {
  const t = m().homeDesk;
  say({
    who: m().objects.homeDesk,
    icon: '✏️',
    text: `${t.text}\n${m().life.desk.worksheet}`,
    choices: [
      {
        text: t.act,
        cost: min(45),
        run: () => {
          if (S().chores.homework) return toast(m().common.alreadyToday);
          openWorksheet('homework', (grade, score) => {
            if (!doChore('homework')) return;
            celebrate(grade === 'A');
            apply({ study: 9 + score * 2, stars: { A: 8, B: 6, C: 4 }[grade], energy: -6, minutes: 45, love: { mom: 10 } });
            toast(t.done);
            completeObjective('homework');
            choreCheck();
            window.setTimeout(openEnglish, 600);
          });
        },
      },
      { text: m().life.desk.portfolio, run: () => openExhibition(false) },
    ],
  });
}

registerObjects({
  homeDesk,
  life_piggy: piggyBank,
  life_adscreen: () => impulseAd('mall'),
});

registerMenuItem(() => m().life.wallet.menu, openWallet);
registerMenuItem(() => m().life.save.menu, () => openSaveCode(false));

/** Exhibition (成果展): every 4th week, on the Friday at school. */
function exhibitionDue(): boolean {
  const s = S();
  return s.week % 4 === 0 && dayId(s) === 'fri' && life().exhibitWeek < s.week;
}

bus.on('dayStart', () => {
  const s = S();
  const g = takeNewGameGrade();
  if (g !== null) s.grade = Math.max(3, Math.min(6, g));
  if (allowanceDue()) whenFree(allowanceDialog, 1200);
  if (dayId(s) === 'sun') whenFree(interestDialog, 1400);
  if (life().wishes.length) whenFree(() => wishMorning(), 1600);
  if (exhibitionDue()) {
    whenFree(() => {
      if (!exhibitionDue()) return;
      life().exhibitWeek = S().week;
      const t = m().life.exhibit;
      say({
        who: t.title,
        icon: '🖼️',
        text: t.today,
        choices: [
          {
            text: t.see,
            run: () => {
              const n = exhibitReward();
              logJournal('🖼️', 'life.journal.exhibit');
              openExhibition(true);
              toast(t.reward(n));
            },
          },
        ],
      });
    }, 2000);
  }
  // Sometimes a phone pop-up ad appears a while into the day.
  if (Math.random() < 0.3) whenFree(() => onceToday('life.ad') && impulseAd('phone'), 45000 + Math.random() * 60000);
});

// Walking into the mall can trigger a big-screen ad (at most one ad a day).
window.setInterval(() => {
  if (!hasGame() || layers.blocking()) return;
  const s = S();
  if (s.player.map !== 'mall' || s.daily['life.mallSeen']) return;
  s.daily['life.mallSeen'] = true;
  if (Math.random() < 0.5 && onceToday('life.ad')) whenFree(() => impulseAd('mall'), 2500);
}, 2000);

